import { useEffect, useState, useRef } from "react";
import { cn } from "tools/ui";
import { FormattedMessage as FM } from "react-intl";

import {
  QuestionIcon,
  WarningIcon,
  XCircleIcon,
  CheckCircleIcon,
} from "components/Icons";

const ANIMATION_DURATION = 300;
const DEFAULT_LIFETIME = 3000;

const TYPES = {
  default: {
    icon: <QuestionIcon className="w-5 h-5" fill="#575358" />,
    colorText: "text-stone-600",
    colorBg: "bg-stone-200",
    border: "border-l-4 border-l-stone-500",
  },
  success: {
    icon: <CheckCircleIcon className="w-5 h-5" fill="#16A34A" />,
    colorText: "text-green-600",
    colorBg: "bg-green-200",
    border: "border-l-4 border-l-green-500",
  },
  error: {
    icon: <XCircleIcon className="w-5 h-5" fill="#DC2626" />,
    colorText: "text-red-600",
    colorBg: "bg-red-200",
    border: "border-l-4 border-l-red-500",
  },
  warning: {
    icon: <WarningIcon className="w-5 h-5" fill="#D97706" />,
    colorText: "text-amber-600",
    colorBg: "bg-amber-200",
    border: "border-l-4 border-l-amber-500",
  },
};

function Message({
  messageOptions,
  onClose,
  show,
  children,
  style,
  lifeTime = DEFAULT_LIFETIME,
  closable = true,
  showCloseIcon = true,
}) {
  const { type, text } = messageOptions.value;
  const currentType = TYPES[type] || TYPES.default;

  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [progress, setProgress] = useState(0);

  const startTimeRef = useRef(0);
  const remainingRef = useRef(lifeTime);
  const pauseRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    if (show) {
      setIsMounted(true);
      requestAnimationFrame(() => setIsVisible(true));
      if (lifeTime > 0) startTimers();
    } else if (!show && isMounted) {
      handleClose();
    }

    return () => stopTimers();
  }, [show]);

  const startTimers = () => {
    startTimeRef.current = Date.now();
    rafRef.current = requestAnimationFrame(updateProgress);
  };

  const updateProgress = () => {
    if (pauseRef.current) {
      rafRef.current = requestAnimationFrame(updateProgress);
      return;
    }

    const elapsed = Date.now() - startTimeRef.current;
    const pct = Math.min((elapsed / lifeTime) * 100, 100);
    setProgress(pct);

    if (pct >= 100) {
      handleClose();
      return;
    }

    rafRef.current = requestAnimationFrame(updateProgress);
  };

  const stopTimers = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const handleMouseEnter = () => {
    if (lifeTime <= 0) return;
    pauseRef.current = true;
    stopTimers();
    const elapsed = Date.now() - startTimeRef.current;
    remainingRef.current = Math.max(0, lifeTime - elapsed);
  };

  const handleMouseLeave = () => {
    if (lifeTime <= 0) return;
    pauseRef.current = false;
    startTimeRef.current = Date.now() - (lifeTime - remainingRef.current);
    rafRef.current = requestAnimationFrame(updateProgress);
  };

  const handleClose = () => {
    stopTimers();
    setIsVisible(false);
    setTimeout(() => {
      setIsMounted(false);
      onClose?.();
    }, ANIMATION_DURATION);
  };

  if (!isMounted) return null;

  const CloseIcon = () => (
    <button
      onClick={handleClose}
      className="shrink-0 ml-2 p-1 rounded hover:bg-white hover:bg-opacity-10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current"
      aria-label="Close message"
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  );

  const ProgressBar = () =>
    lifeTime > 0 && (
      <div className="absolute bottom-0 left-0 w-full h-1 bg-stone-600 bg-opacity-10 rounded-b">
        <div
          className={cn(
            "h-full rounded-b transition-all duration-100",
            type === "success" && "bg-green-500",
            type === "error" && "bg-red-500",
            type === "warning" && "bg-amber-500",
            (!type || type === "default") && "bg-stone-500",
          )}
          style={{
            width: `${progress}%`,
          }}
        />
      </div>
    );

  return (
    <div
      className={cn(
        "z-50 transition-all duration-300 ease-in-out mt-2",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2",
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="alert"
      aria-live="polite"
    >
      <div
        className={cn(
          "p-4 rounded-md flex items-center shadow-lg max-w-sm min-w-80 relative",
          style,
          currentType.colorBg,
          currentType.colorText,
          currentType.border,
          "gap-3",
          "transition-all duration-200 hover:shadow-xl",
        )}
      >
        <div className="shrink-0">{children || currentType.icon}</div>

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium wrap-break-words">
            <FM id={text} />
          </span>
        </div>

        {closable && showCloseIcon && <CloseIcon />}

        <ProgressBar />
      </div>
    </div>
  );
}

export const useMessage = () => {
  const [messages, setMessages] = useState([]);

  const normalizeText = (text) => {
    if (typeof text === "string") return text;
    if (!text) return "";
    if (typeof text === "object") {
      // Try to extract from common error structures
      return text.error || text.message || text.detail || JSON.stringify(text);
    }
    return String(text);
  };

  const show = (options) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const normalizedText = normalizeText(options.text);
    const message = {
      ...options,
      text: normalizedText,
      id,
      lifeTime:
        options.lifeTime ??
        (options.type === "error" ? 5000 : DEFAULT_LIFETIME),
    };
    setMessages((prev) => [...prev, message]);

    return id;
  };

  const hide = (id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  const clear = () => {
    setMessages([]);
  };

  const success = (text, options = {}) => {
    return show({ type: "success", text, ...options });
  };

  const error = (text, options = {}) => {
    return show({ type: "error", text, ...options });
  };

  const warning = (text, options = {}) => {
    return show({ type: "warning", text, ...options });
  };

  const info = (text, options = {}) => {
    return show({ type: "default", text, ...options });
  };

  return {
    show,
    hide,
    clear,
    success,
    error,
    warning,
    info,
    messages,
  };
};

// Componente contenedor para múltiples mensajes
export const MessageContainer = ({
  messages,
  onMessageClose,
  position = "top-right",
}) => {
  const getPositionClasses = () => {
    const positions = {
      "top-right": "top-4 right-4",
      "top-left": "top-4 left-4",
      "top-center": "top-4 left-1/2 transform -translate-x-1/2",
      "bottom-right": "bottom-4 right-4",
      "bottom-left": "bottom-4 left-4",
      "bottom-center": "bottom-4 left-1/2 transform -translate-x-1/2",
      center: "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
    };
    return positions[position] || positions["top-right"];
  };

  function handleOnClose(id) {
    onMessageClose(id);
  }

  return (
    <div className={cn("fixed z-50", getPositionClasses())}>
      {messages.map((message, index) => (
        <Message
          key={message.id}
          messageOptions={{ value: message }}
          position={message.position || position}
          lifeTime={message.lifeTime}
          onClose={handleOnClose}
          show={true}
        />
      ))}
    </div>
  );
};

export default Message;
